import * as React from "react";
import { SettingsSection } from "../layout/section";
import { SettingsSectionBlock } from "../layout/block";
import { SettingsFormDesignThemes } from "../forms/design/themes";
import { SettingsFormColors } from "../forms/design/colors";
import { SettingsFormFonts } from "../forms/design/fonts";
import { SettingsFormDesignTopNav } from "../forms/design/topNav";
import { SettingsFormDesignMenuNavBar } from "../forms/design/menuNavBar";
import { SettingsFormDesignHeader } from "../forms/design/header";
import { SettingsFormDesignItems } from "../forms/design/items";
import { SettingsFormDesignFooter } from "../forms/design/footer";
import { SettingsFormDesignAlert } from "../forms/design/alert";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../../mobx/components";
import { FaArrowRight, FaArrowCircleLeft } from "react-icons/fa";
import { styled } from "@lib/components";
import { toJS } from "mobx";
import ObjectHash from "node-object-hash";
import { SettingsFormDomain } from "../forms/website/domain";
import { SettingsFormWebsiteFavicon } from "../forms/website/favicon";
import { SettingsFormSEO } from "../forms/website/seo";
import { SettingsFormSocialLinks } from "../forms/website/social_links";
import { SettingsSectionHeading } from "../layout/heading";
import { SettingsFormInjections } from "../forms/website/injections";
import { WebsiteFormMisc } from "../forms/website/misc";
import { FaMobileAlt, FaDesktop, FaTabletAlt } from "react-icons/fa";
import { Tooltip } from "@lib/components";
import { SettingsFormDesignMenus } from "../forms/design/itemLayout";
import { SettingsFormDesignMobile } from "../forms/design/mobile";
import { AI_INJECT_SECTION, AskAIEntry, AskAIPanel } from "../forms/ai/ask-ai";

const hasher = ObjectHash();

interface Props {

}
interface State {
	active: string;
	design_mode_active: boolean;
	preview_width: number;
	ai_open: boolean;
	/**
	 * Bumped per section when the assistant commits a change. Used as a form key
	 * so the section remounts and re-reads the store: Formik captures
	 * initialValues once on mount, so without this the panel would keep showing
	 * the pre-AI value and a later Save would write it back over the change.
	 */
	ai_applied: { [section: string]: number };
}

const DesignModeWrapper = styled("div")`
  background: white;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  z-index: 14;
  overflow-x: auto;
  overflow-y: hidden;
  ::-webkit-scrollbar {
    width: 5px;
    max-height: 5px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  ::-webkit-scrollbar-thumb {
    background: #888;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const DesignModeSettings = styled("div") <{ hideScroll: boolean }>`
  background: #f8f8f8;
  max-width: 340px;
  min-width: 320px;
  width: 100%;
  margin-left: 59px;
  padding-bottom: 50px; 
  overflow-y: auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.25);
  ::-webkit-scrollbar {
    width: 5px;
    max-height: 5px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  ::-webkit-scrollbar-thumb {
    background: #888;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const DesignModeIframe = styled("div")`
  z-index: -1;
  background: white;
  width: 100%;
  min-width: 380px;
`;

@inject("store") @observer
export class RestaurantSettingsWebsite extends MobxComponent<Props, State> {

	iframe: HTMLIFrameElement | null = null;
	/** Held so CSS proposed before the iframe bridge is ready is not lost. */
	pending_preview_css: string | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			active: "",
			design_mode_active: false,
			preview_width: -1,
			ai_open: false,
			ai_applied: {},
		};
	}

	componentDidMount() {
		window.addEventListener("message", this.onPreviewReady);
	}

	componentWillUnmount() {
		window.removeEventListener("message", this.onPreviewReady);
	}

	setActive = (active: string) => {
		if (this.state.active === active)
			this.setState({ active: "" });
		else
			this.setState({ active });
	}

	setDesignModeActive = (design_mode_active: boolean) => {
		this.setState({
			design_mode_active,
			active: "",
			ai_open: false,
		});
	}

	setPreviewWidth = (preview_width: number) => {
		this.setState({
			preview_width,
		});
	}

	/**
	 * Ask AI needs the storefront preview to judge a change against, so it opens
	 * Design Mode rather than being available from the plain settings list.
	 */
	openAI = () => {
		this.setState({
			design_mode_active: true,
			// Collapse whatever was open: the chat covers every section, so leaving
			// one expanded would imply the conversation is scoped to it.
			active: "",
			ai_open: true,
		});
	}

	closeAI = () => {
		this.previewCss("");
		this.setState({ ai_open: false });
	}

	onAIApplied = (sections: string[]) => {
		this.setState(state => {
			const ai_applied = { ...state.ai_applied };
			sections.forEach(section => {
				ai_applied[section] = (ai_applied[section] || 0) + 1;
			});
			return { ai_applied };
		});
	}

	formKey = (section: string): string =>
		`${section}-${this.state.ai_applied[section] || 0}`;

	onPreviewReady = (event: MessageEvent) => {
		if (event.origin !== this.injected.store.storeURL) return;
		if (!event.data || event.data.type !== "cw-preview-ready") return;
		if (this.pending_preview_css !== null) {
			this.postPreview(this.pending_preview_css);
			this.pending_preview_css = null;
		}
	}

	/**
	 * Pushes candidate CSS into the storefront iframe so the operator reviews the
	 * change visually. Nothing is saved by doing this.
	 */
	previewCss = (css: string) => {
		if (!this.postPreview(css)) {
			// The iframe reloads whenever settings change, so a proposal can arrive
			// while the bridge is down. Replay it once the storefront announces it.
			this.pending_preview_css = css;
		}
	}

	postPreview = (css: string): boolean => {
		const target = this.iframe && this.iframe.contentWindow;
		if (!target) return false;
		try {
			target.postMessage({ type: "cw-preview-css", css }, this.injected.store.storeURL);
			return true;
		} catch (e) {
			return false;
		}
	}

	render() {

		const { active, design_mode_active, preview_width, ai_open } = this.state;
		const reseller = this.injected.store.reseller;
		const aiEnabled = !!(reseller && reseller.ai_enabled);

		const r = this.injected.store.restaurant!;
		const { storeURL } = this.injected.store;

		const menuTemplate = r.website.sections.menu_nav_bar?.menu_template || "v1";

		// Sits above the Design heading without one of its own: the row is already
		// labelled, and a heading repeating it read as two controls.
		const askAI = aiEnabled ? <AskAIEntry onClick={this.openAI} /> : null;

		const settings = (
			<>

				<SettingsSectionBlock
					name="Themes"
					active={active === "Themes"}
					onClick={() => this.setActive("Themes")}>
					<SettingsFormDesignThemes />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Colors"
					active={active === "Colors"}
					onClick={() => this.setActive("Colors")}>
					<SettingsFormColors key={this.formKey("Colors")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Fonts"
					active={active === "Fonts"}
					onClick={() => this.setActive("Fonts")}>
					<SettingsFormFonts key={this.formKey("Fonts")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Top Nav Bar"
					active={active === "Top Nav Bar"}
					onClick={() => this.setActive("Top Nav Bar")}>
					<SettingsFormDesignTopNav key={this.formKey("Top Nav Bar")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Menu Nav Bar"
					active={active === "Menu Nav Bar"}
					onClick={() => this.setActive("Menu Nav Bar")}>
					<SettingsFormDesignMenuNavBar key={this.formKey("Menu Nav Bar")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Header"
					active={active === "Header"}
					onClick={() => this.setActive("Header")}>
					<SettingsFormDesignHeader key={this.formKey("Header")} />
				</SettingsSectionBlock>

				{(menuTemplate !== "v1") && <SettingsSectionBlock
					name="Item Layout"
					active={active === "Item Layout"}
					onClick={() => this.setActive("Item Layout")}>
					<SettingsFormDesignMenus key={this.formKey("Item Layout")} />
				</SettingsSectionBlock>}

				<SettingsSectionBlock
					name="Items"
					active={active === "Items"}
					onClick={() => this.setActive("Items")}>
					<SettingsFormDesignItems key={this.formKey("Items")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Footer"
					active={active === "Footer"}
					onClick={() => this.setActive("Footer")}>
					<SettingsFormDesignFooter key={this.formKey("Footer")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Mobile"
					active={active === "Mobile"}
					onClick={() => this.setActive("Mobile")}>
					<SettingsFormDesignMobile key={this.formKey("Mobile")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Alert"
					active={active === "Alert"}
					onClick={() => this.setActive("Alert")}>
					<SettingsFormDesignAlert key={this.formKey("Alert")} />
				</SettingsSectionBlock>

				<SettingsSectionHeading>
					<p>Additional Settings</p>
				</SettingsSectionHeading>

				<SettingsSectionBlock
					name="Domain"
					active={active === "Domain"}
					onClick={() => this.setActive("Domain")}>
					<SettingsFormDomain />
				</SettingsSectionBlock>
					
				<SettingsSectionBlock
					name="Favicon"
					active={active === "Favicon"}
					onClick={() => this.setActive("Favicon")}>
					<SettingsFormWebsiteFavicon key={this.formKey("Favicon")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Search Engine Optimization"
					active={active === "Search Engine Optimization"}
					onClick={() => this.setActive("Search Engine Optimization")}>
					<SettingsFormSEO key={this.formKey("SEO")} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Social Media Links"
					active={active === "Social Media Links"}
					onClick={() => this.setActive("Social Media Links")}>
					<SettingsFormSocialLinks />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name={AI_INJECT_SECTION}
					active={active === AI_INJECT_SECTION}
					onClick={() => this.setActive(AI_INJECT_SECTION)}>
					<SettingsFormInjections key={this.formKey(AI_INJECT_SECTION)} />
				</SettingsSectionBlock>

				<SettingsSectionBlock
					name="Misc."
					active={active === "Misc. Website"}
					onClick={() => this.setActive("Misc. Website")}>
					<WebsiteFormMisc />
				</SettingsSectionBlock>

			</>
		);

		const website = {
			subdomain: toJS(r.subdomain),
			domain: toJS(r.domain),
			colors: toJS(r.website.colors),
			fonts: toJS(r.website.fonts),
			sections: toJS(r.website.sections),
			inject: toJS(r.website.inject),
			social_links: toJS(r.website.social_links),
			map_zoom: toJS(r.website.map_zoom),
			bottom_cart_button: toJS(r.website.bottom_cart_button),
			contact_form_email: toJS(r.website.contact_form_email),
			menu_selector_only: toJS(r.website.experimental_ui_1),
		};

		const hash = hasher.hash(website);

		return (
			<>

				{!design_mode_active && (
					<SettingsSection>
						{askAI}
						<SettingsSectionHeading className="flex-l-r-center">
							<p>Design</p>
							<div className="flex-line centered cursor" onClick={() => this.setDesignModeActive(true)}>
								<p className="underline">Open Design Mode</p>
								<FaArrowRight className="m-l-2" />
							</div>
						</SettingsSectionHeading>
						{settings}
					</SettingsSection>
				)}

				{design_mode_active && (
					<DesignModeWrapper>

						<DesignModeSettings hideScroll={true}>
							<div className="flex-l-r-center p-4 bg-white">
								<div className="flex-line centered cursor" onClick={() => this.setDesignModeActive(false)}>
									<FaArrowCircleLeft />
									<p className="big font-semi-bold m-l-2">Go Back</p>
								</div>
								<div className="flex-line centered cursor">
									<Tooltip text="Mobile" width={60} position={"bottom"}>
										<div className="border-white-10 p-2" onClick={() => this.setPreviewWidth(380)}>
											<FaMobileAlt />
										</div>
									</Tooltip>
									<Tooltip text="Tablet" width={60} position={"bottom"}>
										<div className="border-white-10 p-2" onClick={() => this.setPreviewWidth(768)}>
											<FaTabletAlt />
										</div>
									</Tooltip>
									<Tooltip text="Full" width={60} position={"bottom"}>
										<div className="border-white-10 p-2" onClick={() => this.setPreviewWidth(-1)}>
											<FaDesktop />
										</div>
									</Tooltip>
								</div>
							</div>
							<SettingsSection>
								{askAI}
								<SettingsSectionHeading className="border-white-t-10">
									<p>Design</p>
								</SettingsSectionHeading>
								{settings}
							</SettingsSection>
						</DesignModeSettings>

						<DesignModeIframe style={{ minWidth: preview_width === -1 ? "auto" : `${preview_width}px` }}>
							<iframe
								ref={(el) => { this.iframe = el; }}
								onLoadStart={() => console.log("load start")}
								onLoad={() => console.log("load end")}
								className="bsm-2"
								// preview=1 activates the storefront's postMessage listener, which
								// is how Ask AI shows unsaved CSS without writing to the database.
								src={`${storeURL}?h=${hash}&preview=1`}
								width={preview_width === -1 ? "100%" : preview_width}
								height="100%"
							/>
						</DesignModeIframe>

						<AskAIPanel
							open={ai_open}
							onClose={this.closeAI}
							onPreviewCss={this.previewCss}
							onApplied={this.onAIApplied}
						/>
					</DesignModeWrapper>
				)}
			</>

		);
	}

}
